import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLanguage } from '../../i18n';
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
  alpha,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Collapse,
  IconButton,
  Tooltip,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Email as EmailIcon,
  Assessment as ReportIcon,
  LocalShipping as PalletIcon,
  Description as DocIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { uploadVoucher, simulateEmailReceive, runReconciliation, sendReconciliationEmail, downloadReconciliationReport, uploadToFirebase, processDocument } from '../../services/api';
import { FieldValue, ComparisonResult, Classification } from '../../types';
import FirebaseSelector, { ReprocessResult } from '../shared/FirebaseSelector';
import DocumentPreview from '../shared/DocumentPreview';
import ProcessingStatus, { ProcessingStep as PSProcessingStep } from '../ProcessingStatus';

// Processing steps — same as Tab 1
const initialSteps: PSProcessingStep[] = [
  { label: 'Document Agent', description: 'Uploading document and preparing for pipeline processing', status: 'pending' },
  { label: 'OCR Extraction Agent', description: 'Extracting text and structured fields using Document AI + Gemini', status: 'pending' },
  { label: 'ERP Comparison Agent', description: 'Matching extracted fields against ERP records from data bucket', status: 'pending' },
  { label: 'Classification Agent', description: 'Analyzing results and classifying as standard or exception case', status: 'pending' },
];

function PalletVoucherUpload() {
  const { t } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [dragActive, setDragActive] = useState(false);

  // Processing state
  const [processingSteps, setProcessingSteps] = useState<PSProcessingStep[]>(initialSteps);
  const [showSteps, setShowSteps] = useState(true);
  const [typewriterText, setTypewriterText] = useState('');
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);

  // Extracted data state
  const [extractedData, setExtractedData] = useState<any>(null);
  const [palletSummary, setPalletSummary] = useState<any>(null);
  const [voucherId, setVoucherId] = useState<string | null>(null);

  // Reconciliation state
  const [reconciliationReport, setReconciliationReport] = useState<any>(null);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [runningReconciliation, setRunningReconciliation] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);

  // Email simulation state
  const [sender, setSender] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult[] | null>(null);
  const [classification, setClassification] = useState<Classification | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);

  // Processing status state (same as Tab 1)
  const [currentStep, setCurrentStep] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isProcessingActive, setIsProcessingActive] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Timer effect
  useEffect(() => {
    if (isProcessingActive) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isProcessingActive]);

  // Typewriter effect
  useEffect(() => {
    if (currentStepIndex >= 0 && currentStepIndex < processingSteps.length) {
      const step = processingSteps[currentStepIndex];
      if (step.status === 'running') {
        const text = step.description;
        let index = 0;
        setTypewriterText('');
        const interval = setInterval(() => {
          if (index < text.length) {
            setTypewriterText(text.substring(0, index + 1));
            index++;
          } else {
            clearInterval(interval);
          }
        }, 30);
        return () => clearInterval(interval);
      }
    }
  }, [currentStepIndex, processingSteps]);

  // Update step status helper
  const updateStep = (stepIndex: number, status: PSProcessingStep['status'], duration?: number) => {
    setProcessingSteps(prev => prev.map((step, idx) => 
      idx === stepIndex ? { ...step, status, duration } : step
    ));
    if (status === 'running') setCurrentStep(stepIndex);
  };

  const resetSteps = () => {
    setProcessingSteps(initialSteps.map(s => ({ ...s })));
    setCurrentStep(0);
    setElapsedTime(0);
    setIsProcessingActive(false);
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setError(null);
    }
  }, []);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
      setError(null);
    }
  };

  const handleAttachmentsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setAttachments(Array.from(event.target.files));
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);
    resetSteps();
    setExtractedData(null);
    setPalletSummary(null);
    setVoucherId(null);
    setComparisonResult(null);
    setClassification(null);
    setIsProcessingActive(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Step 1: Upload to Firebase & OCR Extract
      const step1Start = Date.now();
      updateStep(0, 'running');

      console.log('[Pallet] Calling uploadToFirebase...');
      const uploadResponse = await uploadToFirebase(formData);
      console.log('[Pallet] Upload response:', uploadResponse);

      const step1Duration = Math.round((Date.now() - step1Start) / 1000);
      updateStep(0, 'completed', step1Duration);

      // Step 2: OCR Processing
      updateStep(1, 'running');
      await new Promise(resolve => setTimeout(resolve, 400));
      updateStep(1, 'completed', 1);

      // Step 3: Field Extraction
      updateStep(2, 'running');
      await new Promise(resolve => setTimeout(resolve, 400));
      updateStep(2, 'completed', 1);

      // Set extracted data from upload response
      const docId = uploadResponse.document_id;
      setDocumentId(docId);
      setVoucherId(docId);

      if (uploadResponse.extracted_data) {
        const ed = uploadResponse.extracted_data as any;
        setExtractedData({
          ...ed,
          all_fields: ed.all_fields || ed.field_details || {},
          field_details: ed.field_details || ed.all_fields || {},
          detected_language: ed.detected_language || 'unknown',
          total_fields_extracted: ed.total_fields_extracted ||
            Object.keys(ed.all_fields || ed.field_details || {}).length,
        });
        if (ed.pallet_summary) {
          setPalletSummary(ed.pallet_summary);
        }
      }

      // Step 4: ERP Comparison via processDocument (same as Tab 1)
      updateStep(3, 'running');
      try {
        console.log('[Pallet] Processing document for ERP comparison:', docId);
        const processResponse = await processDocument(docId);
        console.log('[Pallet] Process response:', processResponse);

        if (processResponse.comparison_result) {
          setComparisonResult(processResponse.comparison_result);
        }
        if (processResponse.classification) {
          setClassification(processResponse.classification);
        }
        updateStep(3, 'completed', 1);
      } catch (processErr) {
        console.warn('[Pallet] ERP comparison failed (extraction still succeeded):', processErr);
        updateStep(3, 'completed', 1);
      }

      // Step 5: Status Determination
      updateStep(4, 'running');
      await new Promise(resolve => setTimeout(resolve, 300));
      updateStep(4, 'completed', 1);

      const quantity = palletSummary?.pallet_quantity || 0;
      const status = palletSummary?.pallet_status || 'processed';

      setSuccess(`✅ Voucher uploaded, extracted & compared with ERP successfully! ${quantity} pallet(s) - Status: ${status}`);
    } catch (err: any) {
      console.error('Upload error:', err);
      const failedStepIndex = processingSteps.findIndex(s => s.status === 'running');
      if (failedStepIndex >= 0) {
        updateStep(failedStepIndex, 'error');
      }
      setError(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setIsProcessingActive(false);
    }
  };

  const handleEmailSimulate = async () => {
    if (!sender || !subject || attachments.length === 0) {
      setError('Please fill all required fields and attach files');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await simulateEmailReceive(sender, subject, body, attachments);
      setSuccess(
        `Email processed. Vouchers created: ${result.voucher_ids.length}`
      );
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Email simulation failed');
    } finally {
      setUploading(false);
    }
  };

  // Handle run reconciliation
  const handleRunReconciliation = async () => {
    setRunningReconciliation(true);
    setError(null);
    try {
      const result = await runReconciliation('CUST-001', '2025-01-01', '2025-01-31');
      setReconciliationReport(result);
      setSuccess('Reconciliation completed successfully!');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Reconciliation failed');
    } finally {
      setRunningReconciliation(false);
    }
  };

  // Handle send email
  const handleSendEmail = async () => {
    if (!recipientEmail) {
      setError('Please enter a recipient email address');
      return;
    }
    setSendingEmail(true);
    setError(null);
    try {
      const result = await sendReconciliationEmail(recipientEmail, 'CUST-001');
      setSuccess(`Email report sent to ${recipientEmail}! Subject: ${result.email_subject}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Handle download report
  const handleDownloadReport = async (format: 'json' | 'html' | 'txt') => {
    setDownloadingReport(true);
    setError(null);
    try {
      const reconciliationId = reconciliationReport?.reconciliation_id || 'report';
      const blob = await downloadReconciliationReport(reconciliationId, format);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reconciliation_report_${reconciliationId}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSuccess(`Report downloaded successfully as ${format.toUpperCase()}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to download report');
    } finally {
      setDownloadingReport(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 2, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)',
          }}
        >
          <PalletIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {t.palletPage.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t.palletPage.subtitle}
          </Typography>
        </Box>
      </Box>

      {/* Firebase Document Selector & ERP Data — wraps upload area as children */}
      <FirebaseSelector
        processType="pallet"
        disabled={uploading}
        onReprocessComplete={(docId: string, result: ReprocessResult) => {
          console.log('[PalletVoucher] Reprocess complete:', docId, result);
          setVoucherId(docId);
          if (result.extracted_data) {
            setExtractedData(result.extracted_data);
            if (result.extracted_data.pallet_summary) {
              setPalletSummary(result.extracted_data.pallet_summary);
            }
          }
          if (result.comparison_result) {
            // If comparison result contains pallet summary info
            const palletInfo = Array.isArray(result.comparison_result) 
              ? result.comparison_result.find((r: any) => r.pallet_summary) 
              : result.comparison_result;
            if (palletInfo?.pallet_summary) {
              setPalletSummary(palletInfo.pallet_summary);
            }
          }
          setSuccess('✅ Document reprocessed with ERP comparison');
          setError(null);
        }}
      >
        {/* Divider between Firebase selector and upload */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Divider sx={{ flex: 1 }} />
          <Chip
            label="Or upload new"
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              color: 'text.secondary',
              borderColor: 'divider',
            }}
          />
          <Divider sx={{ flex: 1 }} />
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, v) => setTabValue(v)}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
              },
            }}
          >
            <Tab icon={<UploadIcon />} label={t.palletReconciliation.uploadVoucher} iconPosition="start" />
            <Tab icon={<EmailIcon />} label={t.palletReconciliation.emailImport} iconPosition="start" />
            <Tab icon={<ReportIcon />} label={t.palletReconciliation.reconciliation} iconPosition="start" />
          </Tabs>
        </Paper>
      </FirebaseSelector>

      {/* Tab 0: Manual Upload */}
      {tabValue === 0 && (
        <Box>
          {/* Drag & Drop Upload Area */}
          <Box
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            sx={{
              border: '2px dashed',
              borderColor: dragActive ? 'secondary.main' : file ? 'secondary.main' : 'divider',
              borderRadius: 3,
              p: 4,
              textAlign: 'center',
              backgroundColor: dragActive ? alpha('#8B5CF6', 0.04) : file ? alpha('#8B5CF6', 0.04) : 'transparent',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'secondary.main',
                backgroundColor: alpha('#8B5CF6', 0.04),
                transform: 'scale(1.01)',
              },
            }}
          >
            <input
              accept="image/*,.pdf"
              style={{ display: 'none' }}
              id="voucher-file-input"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="voucher-file-input" style={{ cursor: 'pointer', display: 'block' }}>
              {file ? (
                <Box>
                  <Box sx={{ width: 64, height: 64, borderRadius: 2, backgroundColor: alpha('#8B5CF6', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <FileIcon sx={{ fontSize: 32, color: '#8B5CF6' }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{file.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{formatFileSize(file.size)}</Typography>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ width: 64, height: 64, borderRadius: 2, backgroundColor: alpha('#8B5CF6', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <UploadIcon sx={{ fontSize: 32, color: '#8B5CF6' }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {dragActive ? t.palletPage.dropVoucher : t.palletPage.dragDropVoucher}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t.palletPage.browseFormats}
                  </Typography>
                </Box>
              )}
            </label>
          </Box>

          {/* Upload Button + Preview */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3, alignItems: 'center' }}>
            {file && <DocumentPreview file={file} compact />}
            <Button
              variant="contained"
              size="large"
              onClick={handleUpload}
              disabled={!file || uploading}
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
              fullWidth
              sx={{
                background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(139, 92, 246, 0.4)',
                },
              }}
            >
              {uploading ? t.common.processing : t.palletPage.uploadProcess}
            </Button>
          </Box>

          {/* Processing Status with Agent Steps — same as Tab 1 */}
          <ProcessingStatus
            isProcessing={isProcessingActive}
            currentStep={currentStep}
            steps={processingSteps}
            elapsedTime={elapsedTime}
            estimatedTime={120}
          />

          {/* Extracted Data Table */}
          {extractedData && extractedData.all_fields && Object.keys(extractedData.all_fields).length > 0 && (
            <Paper sx={{ mt: 3, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{t.palletReconciliation.extractedFields}</Typography>
                <Chip label={`${extractedData.total_fields_extracted || Object.keys(extractedData.all_fields).length} fields`} size="small" color="secondary" />
              </Box>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: alpha('#8B5CF6', 0.1) }}>
                      <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Language</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Original Text</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>English Text</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Normalized</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Confidence</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(extractedData.all_fields).map(([key, fieldValue]) => {
                      const value = fieldValue as FieldValue;
                      return (
                        <TableRow key={key} sx={{ '&:hover': { backgroundColor: alpha('#8B5CF6', 0.05) } }}>
                          <TableCell><strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong></TableCell>
                          <TableCell><Chip label={(value?.detected_language || 'N/A').toUpperCase()} size="small" /></TableCell>
                          <TableCell>{value?.original_text || value?.original || 'N/A'}</TableCell>
                          <TableCell>{value?.english_text || value?.translated || 'N/A'}</TableCell>
                          <TableCell><strong>{value?.normalized || 'N/A'}</strong></TableCell>
                          <TableCell>
                            {(() => {
                              const conf = value?.confidence;
                              if (conf == null) return <Chip label="N/A" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
                              const pct = Math.round(conf * 100);
                              const color = conf >= 0.8 ? '#10B981' : conf >= 0.5 ? '#F59E0B' : '#EF4444';
                              return <Chip label={`${pct}%`} size="small" sx={{ backgroundColor: alpha(color, 0.15), color, fontWeight: 700, fontSize: '0.75rem', minWidth: 48 }} />;
                            })()}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* 📊 Comparison with ERP Data — LLM-based (same as Tab 1) */}
          {comparisonResult && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  📊 Comparison with ERP Data
                </Typography>
                {(() => {
                  const withDataResults = comparisonResult.filter(r => r.document_value || r.erp_value);
                  const matchCount = withDataResults.filter(r => r.match).length;
                  const totalCount = withDataResults.length;
                  const matchPct = totalCount > 0 ? Math.round((matchCount / totalCount) * 100) : 0;
                  return (
                    <>
                      <Chip label={`${matchCount}/${totalCount} fields match`} size="small" color={matchPct === 100 ? 'success' : matchPct >= 50 ? 'warning' : 'error'} sx={{ fontWeight: 600 }} />
                      <Chip label={`${matchPct}% match rate`} size="small" variant="outlined" sx={{ borderColor: matchPct === 100 ? '#10B981' : matchPct >= 50 ? '#F59E0B' : '#EF4444', color: matchPct === 100 ? '#10B981' : matchPct >= 50 ? '#F59E0B' : '#EF4444', fontWeight: 600 }} />
                    </>
                  );
                })()}
              </Box>
              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper', overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: alpha('#8B5CF6', 0.1) }}>
                      <TableCell sx={{ fontWeight: 700, width: '20%' }}>Field</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '25%' }}>📄 Document Value</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '25%' }}>🏢 ERP Value</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '15%', textAlign: 'center' }}>Match Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '15%' }}>Discrepancy</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comparisonResult.map((result) => {
                      const bothEmpty = !result.document_value && !result.erp_value;
                      const isMatch = result.match && !bothEmpty;
                      const isNoData = bothEmpty;
                      return (
                        <TableRow key={result.field} sx={{ backgroundColor: isNoData ? alpha('#6B7280', 0.03) : isMatch ? alpha('#10B981', 0.03) : alpha('#EF4444', 0.05), '&:hover': { backgroundColor: isNoData ? alpha('#6B7280', 0.08) : isMatch ? alpha('#10B981', 0.08) : alpha('#EF4444', 0.1) }, transition: 'background-color 0.2s ease' }}>
                          <TableCell><strong>{result.field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong></TableCell>
                          <TableCell sx={{ color: result.document_value ? 'text.primary' : 'text.disabled', fontFamily: result.document_value ? 'monospace' : 'inherit' }}>{result.document_value || '—  Not found'}</TableCell>
                          <TableCell sx={{ color: result.erp_value ? 'text.primary' : 'text.disabled', fontFamily: result.erp_value ? 'monospace' : 'inherit' }}>{result.erp_value || '—  Not available'}</TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {isNoData ? <Chip label="⚠ No Data" size="small" sx={{ backgroundColor: alpha('#6B7280', 0.15), color: '#9CA3AF', fontWeight: 700, fontSize: '0.75rem' }} />
                              : isMatch ? <Chip label="✓ Match" size="small" sx={{ backgroundColor: alpha('#10B981', 0.15), color: '#10B981', fontWeight: 700, fontSize: '0.75rem' }} />
                              : <Chip label="✗ Mismatch" size="small" sx={{ backgroundColor: alpha('#EF4444', 0.15), color: '#EF4444', fontWeight: 700, fontSize: '0.75rem' }} />}
                          </TableCell>
                          <TableCell>
                            {isMatch ? <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 700, fontSize: '1rem' }}>✓</Typography>
                              : isNoData ? <Typography variant="caption" color="text.disabled">—</Typography>
                              : result.discrepancy_type ? <Chip label={result.discrepancy_type.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22, borderColor: alpha('#F59E0B', 0.6), color: '#F59E0B' }} />
                              : <Typography variant="caption" color="text.disabled">—</Typography>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Pallet Summary */}
          {palletSummary && (
            <Paper sx={{ mt: 3, p: 3, background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.05) 100%)', border: '1px solid', borderColor: 'secondary.main', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PalletIcon sx={{ color: '#8B5CF6' }} />
                {t.palletReconciliation.palletSummary}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Card sx={{ background: alpha('#8B5CF6', 0.1), border: 'none', boxShadow: 'none' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#8B5CF6' }}>
                        {palletSummary.pallet_quantity || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{t.palletReconciliation.pallets}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card sx={{ background: alpha(palletSummary.pallet_status === 'returned' ? '#10B981' : palletSummary.pallet_status === 'stayed_with_customer' ? '#F59E0B' : '#6B7280', 0.1), border: 'none', boxShadow: 'none' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Chip 
                        label={palletSummary.pallet_status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'} 
                        size="small"
                        sx={{ 
                          backgroundColor: palletSummary.pallet_status === 'returned' ? '#10B981' : palletSummary.pallet_status === 'stayed_with_customer' ? '#F59E0B' : '#6B7280',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t.palletReconciliation.status}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card sx={{ background: alpha('#3B82F6', 0.1), border: 'none', boxShadow: 'none' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#3B82F6' }}>
                        {palletSummary.customer_name || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{t.palletReconciliation.customer}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card sx={{ background: alpha('#10B981', 0.1), border: 'none', boxShadow: 'none' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#10B981' }}>
                        {palletSummary.receive_date || palletSummary.issue_date || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{t.palletReconciliation.date}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              {palletSummary.status_reasoning && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>{t.palletReconciliation.aiAnalysis}:</strong> {palletSummary.status_reasoning}
                </Alert>
              )}
            </Paper>
          )}

        </Box>
      )}

      {/* Tab 1: Email Import */}
      {tabValue === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            {t.palletReconciliation.importFromEmail}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField fullWidth label={t.palletReconciliation.senderEmail} value={sender} onChange={(e) => setSender(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label={t.palletReconciliation.subject} value={subject} onChange={(e) => setSubject(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label={t.palletReconciliation.emailBody} multiline rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <input accept="image/*,.pdf" style={{ display: 'none' }} id="email-attachments-input" type="file" multiple onChange={handleAttachmentsChange} />
              <label htmlFor="email-attachments-input">
                <Button variant="outlined" component="span" fullWidth startIcon={<FileIcon />}>
                  {attachments.length > 0 ? `${attachments.length} ${t.palletReconciliation.filesSelected}` : t.palletReconciliation.selectAttachments}
                </Button>
              </label>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={handleEmailSimulate} disabled={uploading} fullWidth startIcon={uploading ? <CircularProgress size={20} /> : <EmailIcon />}
                sx={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', '&:hover': { background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)' } }}>
                {uploading ? t.common.processing : t.palletReconciliation.processEmailVouchers}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Tab 2: Reconciliation */}
      {tabValue === 2 && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReportIcon sx={{ color: '#8B5CF6' }} />
              {t.palletReconciliation.runReconciliation}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t.palletReconciliation.reconciliationDesc}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t.palletReconciliation.recipientEmail} placeholder="email@example.com" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Button variant="contained" onClick={handleRunReconciliation} disabled={runningReconciliation} fullWidth size="large"
                  startIcon={runningReconciliation ? <CircularProgress size={20} color="inherit" /> : <ReportIcon />}
                  sx={{ height: '56px', background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', '&:hover': { background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)' } }}>
                  {runningReconciliation ? t.palletReconciliation.runningAnalysis : t.palletReconciliation.runAnalysis}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Reconciliation Report */}
          {reconciliationReport && (
            <Paper sx={{ p: 3, background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(124, 58, 237, 0.02) 100%)', border: '1px solid', borderColor: 'secondary.main', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                📊 {t.palletReconciliation.reconciliationReport}
              </Typography>
              
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={2}>
                  <Card sx={{ background: alpha('#8B5CF6', 0.1), textAlign: 'center' }}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#8B5CF6' }}>
                        {reconciliationReport.voucher_analysis?.total_vouchers || 0}
                      </Typography>
                      <Typography variant="body2">{t.palletReconciliation.vouchers}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Card sx={{ background: alpha('#3B82F6', 0.1), textAlign: 'center' }}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#3B82F6' }}>
                        {reconciliationReport.voucher_analysis?.total_pallets_sent || 0}
                      </Typography>
                      <Typography variant="body2">{t.palletReconciliation.totalPallets}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Card sx={{ background: alpha('#10B981', 0.1), textAlign: 'center' }}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#10B981' }}>
                        {reconciliationReport.voucher_analysis?.pallets_returned || 0}
                      </Typography>
                      <Typography variant="body2">{t.palletReconciliation.returned}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Card sx={{ background: alpha('#F59E0B', 0.1), textAlign: 'center' }}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#F59E0B' }}>
                        {reconciliationReport.voucher_analysis?.pallets_stayed_with_customer || 0}
                      </Typography>
                      <Typography variant="body2">{t.palletReconciliation.withCustomer}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Card sx={{ background: alpha(reconciliationReport.inventory_comparison?.divergence !== 0 ? '#EF4444' : '#10B981', 0.1), textAlign: 'center' }}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: reconciliationReport.inventory_comparison?.divergence !== 0 ? '#EF4444' : '#10B981' }}>
                        {reconciliationReport.inventory_comparison?.divergence || 0}
                      </Typography>
                      <Typography variant="body2">{t.palletReconciliation.divergence}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Card sx={{ background: alpha('#EF4444', 0.1), textAlign: 'center' }}>
                    <CardContent>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#EF4444' }}>
                        R$ {reconciliationReport.inventory_comparison?.financial_impact_brl?.toFixed(2) || '0.00'}
                      </Typography>
                      <Typography variant="body2">{t.palletReconciliation.financialImpact}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* AI Analysis */}
              {reconciliationReport.ai_analysis && (
                <Paper sx={{ p: 2, mb: 3, background: alpha(reconciliationReport.ai_analysis.risk_level === 'high' || reconciliationReport.ai_analysis.risk_level === 'critical' ? '#EF4444' : reconciliationReport.ai_analysis.risk_level === 'medium' ? '#F59E0B' : '#10B981', 0.1), borderLeft: '4px solid', borderColor: reconciliationReport.ai_analysis.risk_level === 'high' || reconciliationReport.ai_analysis.risk_level === 'critical' ? '#EF4444' : reconciliationReport.ai_analysis.risk_level === 'medium' ? '#F59E0B' : '#10B981' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    ⚡ AI Analysis - Risk Level: {reconciliationReport.ai_analysis.risk_level?.toUpperCase()}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>{reconciliationReport.ai_analysis.summary}</Typography>
                  
                  {reconciliationReport.ai_analysis.key_findings?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t.palletReconciliation.keyFindings}:</Typography>
                      {reconciliationReport.ai_analysis.key_findings.map((f: string, i: number) => (
                        <Chip key={i} label={f} size="small" sx={{ m: 0.5 }} />
                      ))}
                    </Box>
                  )}
                  
                  {reconciliationReport.ai_analysis.recommended_actions?.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t.palletReconciliation.recommendedActions}:</Typography>
                      {reconciliationReport.ai_analysis.recommended_actions.map((a: string, i: number) => (
                        <Typography key={i} variant="body2" sx={{ ml: 2 }}>→ {a}</Typography>
                      ))}
                    </Box>
                  )}
                </Paper>
              )}

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
                <Button 
                  variant="contained" 
                  startIcon={sendingEmail ? <CircularProgress size={20} color="inherit" /> : <SendIcon />} 
                  onClick={handleSendEmail}
                  disabled={sendingEmail || !recipientEmail}
                  sx={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', '&:hover': { background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)' } }}
                >
                  {sendingEmail ? 'Sending...' : 'Send Email Report'}
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={downloadingReport ? <CircularProgress size={20} /> : <DocIcon />} 
                  onClick={() => handleDownloadReport('json')}
                  disabled={downloadingReport}
                  sx={{ borderColor: '#3B82F6', color: '#3B82F6' }}
                >
                  Download JSON
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={downloadingReport ? <CircularProgress size={20} /> : <DocIcon />} 
                  onClick={() => handleDownloadReport('html')}
                  disabled={downloadingReport}
                  sx={{ borderColor: '#10B981', color: '#10B981' }}
                >
                  Download HTML
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<EmailIcon />} 
                  onClick={() => setShowEmailPreview(!showEmailPreview)}
                  sx={{ borderColor: '#8B5CF6', color: '#8B5CF6' }}
                >
                  {showEmailPreview ? 'Hide Preview' : 'Preview Email'}
                </Button>
              </Box>

              {/* Email Preview */}
              {showEmailPreview && (
                <Paper sx={{ mt: 3, p: 3, backgroundColor: '#f9f9f9', border: '1px solid #ddd' }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>📧 Email Preview</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>To:</strong> {recipientEmail || 'Enter email above'}</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}><strong>Subject:</strong> Pallet Reconciliation Report - Customer CUST-001</Typography>
                  <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #eee' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {`PALLET RECONCILIATION REPORT
============================

Customer: Customer CUST-001
Period: Last 30 days

SUMMARY
-------
• Vouchers Processed: ${reconciliationReport?.voucher_analysis?.total_vouchers || 0}
• Total Pallets: ${reconciliationReport?.voucher_analysis?.total_pallets_sent || 0}
• Returned: ${reconciliationReport?.voucher_analysis?.pallets_returned || 0}
• With Customer: ${reconciliationReport?.voucher_analysis?.pallets_stayed_with_customer || 0}
• Divergence: ${reconciliationReport?.inventory_comparison?.divergence || 0} pallets
• Financial Impact: R$ ${reconciliationReport?.inventory_comparison?.financial_impact_brl?.toFixed(2) || '0.00'}

AI ANALYSIS
-----------
Risk Level: ${reconciliationReport?.ai_analysis?.risk_level?.toUpperCase() || 'N/A'}
${reconciliationReport?.ai_analysis?.summary || 'No analysis available'}

---
JBS Pallet Reconciliation System`}
                    </Typography>
                  </Box>
                </Paper>
              )}
            </Paper>
          )}
        </Box>
      )}

      {/* Messages */}
      {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 3 }}>{success}</Alert>}
    </Box>
  );
}

export default PalletVoucherUpload;

